/**
 * Mobile Free Agent Search - Column Definitions
 * Compact, data-dense columns for 2-row format (season + rolling)
 */

import React from 'react';
import { dynastyTheme, withAlpha } from '../../../../services/colorService';
import MobileInjuryBadge from '../../components/MobileInjuryBadge';

import MobilePlayerLink from '../../components/MobilePlayerLink';
// 9o.6.e Commit 8: stats window for rolling row + sort key path. MobileDynastyTable
// sorts via dot-path key lookup (getNestedValue), so window awareness means
// constructing keys against the active window's stats slot.
import {
  STATS_KEY_FOR_RANGE,
  getRollingStatsForWindow,
  getRollingRowLabel,
} from '../../../../utils/playerStatsWindow';
// Bible § 14 (ratified 2026-05-12): per-league stat tables MUST derive
// their stat columns from league_settings.scoring_categories via
// getStatConfigs + buildDynamicStatColumn (shared with mobileRosterColumns
// and mobileTeamStatsColumns). FA Search threads statsSlot + dateRange-
// aware getRollingStats + styledRender for the rolling-row visual cues.
import { buildDynamicStatColumn } from '../../../../utils/dynamicStatColumn';
import { statFieldSimple } from '../../../../utils/statColumnDedup';

const C = dynastyTheme.tokens.colors;

// =============================================================================
// HITTER COLUMNS (2-Row Format)
// =============================================================================

export const createMobileHitterColumns = ({
  onAddPlayer,
  addingPlayer,
  showAll = false,
  bulkMode = false,
  isPlayerSelected,
  togglePlayerSelection,
  isCommissionerMode = false,
  activeTeamName = '',
  myTeamId = null,
  dateRange = 'season',
  onPlayerClick = null,
  statConfigs = [],
}) => {
  // 9o.6.e Commit 8: build column `key` paths against the active stats window.
  // Mobile sort is dot-path driven (getNestedValue on row), so the key IS the
  // sort accessor — making the key window-aware aligns client sort with server.
  const statsSlot = STATS_KEY_FOR_RANGE[dateRange] || 'season';
  const k = (category, field) => `stats.${statsSlot}.${category}.${field}`;

  const columns = [];

  // Checkbox column (bulk mode only)
  if (bulkMode) {
    columns.push({
      key: 'checkbox',
      title: '☑',
      width: 24,
      sticky: true,
      cellPadding: 'py-0 px-0',
      textAlign: 'text-center',
      sortable: false,
      render: (player, rowType) => {
        if (rowType === 'rolling') return null; // Only show on season row

        const isOwned = player.league_context?.team?.team_name;
        const isSelected = isPlayerSelected?.(player);

        if (isOwned) {
          return (
            <div style={{
              width: '24px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.3
            }}>
              <input
                type="checkbox"
                disabled
                style={{ width: '14px', height: '14px', cursor: 'not-allowed' }}
              />
            </div>
          );
        }

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              togglePlayerSelection?.(player);
            }}
            style={{
              width: '24px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                togglePlayerSelection?.(player);
              }}
              style={{ width: '14px', height: '14px', cursor: 'pointer' }}
            />
          </div>
        );
      }
    });
  }

  // POS Column (Sticky)
  columns.push({
    key: 'position_slot',
    title: 'POS',
    width: 22,
    sticky: true,
    cellPadding: 'py-0 px-0',
    textAlign: 'text-center',
    sortable: false,
    render: (player, rowType) => {
      if (rowType === 'rolling') return null;

      const position = player.info?.position || '-';
      return (
        <span style={{
          color: dynastyTheme.tokens.colors.warning,
          fontWeight: '700',
          fontSize: '9px'
        }}>
          {position}
        </span>
      );
    }
  });

  // Player Name Column (Sticky). Width 130 accommodates long names
  // (e.g., "Andrés Giménez") plus inline waiver + injury badges without
  // truncation; previous 100 clipped names + badges.
  columns.push({
    key: 'player_name',
    title: 'Player',
    width: 130,
    sticky: true,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-left',
    sortable: true,
    sortValue: (p) => `${p?.info?.last_name || ''} ${p?.info?.first_name || ''}`.trim(),
    render: (player, rowType) => {
      if (rowType === 'season') {
        const firstName = player.info?.first_name || '';
        const lastName = player.info?.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const isOnWaivers = player.league_context?.availability?.on_waivers === true
          || player.league_context?.availability?.status === 'waivers';
        const waiverLabel = player.league_context?.availability?.waiver_label || 'W';

        return (
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
          }}>
            <MobilePlayerLink
              mlbId={player.ids?.mlb}
              name={fullName}
              onClick={onPlayerClick ? () => onPlayerClick(player) : null}
              style={{ fontSize: 11, fontWeight: 500 }}
            />
            {isOnWaivers && (
              <span style={{ flexShrink: 0, fontSize: 8, color: C.orange.soft, border: `1px solid ${withAlpha(C.orange.soft, 0.5)}`, borderRadius: 2, padding: '0 2px', fontStyle: 'normal', whiteSpace: 'nowrap' }}>
                {waiverLabel}
              </span>
            )}
            <MobileInjuryBadge mlbId={player.ids?.mlb} />
          </span>
        );
      } else {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.warning,
            fontSize: '9px',
            fontStyle: 'italic',
            opacity: 0.6
          }}>
            {getRollingRowLabel(dateRange) || 'Last 14'}
          </span>
        );
      }
    }
  });

  // MLB Team Column — width 22 (down from 26) to partially offset the
  // Player widen; 3-char codes (LAA, NYM) fit at fontSize 9px monospace.
  columns.push({
    key: 'mlb_team',
    title: 'Tm',
    width: 22,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: false,
    render: (player, rowType) => {
      if (rowType === 'rolling') return null;

      const team = player.info?.mlb_team || '-';
      return (
        <span style={{
          color: dynastyTheme.tokens.colors.neutral[400],
          fontSize: '9px'
        }}>
          {team}
        </span>
      );
    }
  });

  // Games Played
  columns.push({
    key: k('hitting', 'games_played'),
    title: 'G',
    width: 22,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: true,
    render: (player, rowType) => {
      const stats = rowType === 'season'
        ? player.stats?.season?.hitting
        : getRollingStatsForWindow(player, dateRange)?.hitting;
      const value = stats?.games_played ?? 0;

      const colorClass = rowType === 'season' ? C.white : dynastyTheme.tokens.colors.warning;
      const fontStyle = rowType === 'season' ? 'normal' : 'italic';
      const opacity = rowType === 'season' ? 1 : 0.6;

      return (
        <span style={{
          color: colorClass,
          fontSize: '10px',
          fontStyle: fontStyle,
          opacity: opacity,
          fontFamily: 'monospace'
        }}>
          {value}
        </span>
      );
    }
  });

  // === Dynamic scoring-category columns (Bible § 14 — driven by
  // league_settings.scoring_categories). statsSlot threads the active
  // dateRange into column keys so sort + the value accessor resolve to
  // the right rolling window. getRollingStats hooks the FA-Search
  // dateRange-aware lookup (last7/last14/last30/custom). styledRender
  // preserves the italic/yellow rolling-row visual cue.
  // DD-STAT-CONSOLIDATION sweep: exclude dynamic G — the static column renders it.
  statConfigs
    .filter((c) => c.category === 'hitting' && statFieldSimple(c) !== 'games_played')
    .forEach((c) => {
      columns.push(
        buildDynamicStatColumn(c, {
          statsSlot,
          getRollingStats: (p, cat) => getRollingStatsForWindow(p, dateRange)?.[cat],
          styledRender: true,
        }),
      );
    });

  // Team Column (All Players mode only)
  if (showAll) {
    columns.push({
      key: 'fantasy_team',
      title: 'Team',
      width: 65,
      cellPadding: 'py-0 px-1',
      textAlign: 'text-left',
      sortable: false,
      render: (player, rowType) => {
        if (rowType === 'rolling') return null;

        const teamName = player.league_context?.team?.team_name || '';
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.neutral[400],
            fontSize: '9px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {teamName}
          </span>
        );
      }
    });
  }

  // Price / MV / Add render on the always-visible season row.
  // Matches desktop TableColumns.js gating; rolling row holds rolling stats only.
  columns.push({
    key: 'price',
    title: 'Price',
    width: 42,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: true,
    sortValue: (p) => p?.league_context?.financial?.baseline_price ?? -Infinity,
    render: (player, rowType) => {
      if (rowType !== 'season') return null;

      const amount = player.league_context?.financial?.baseline_price;

      if (amount === null || amount === undefined) {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.neutral[500],
            fontSize: '10px',
            fontFamily: 'monospace'
          }}>
            -
          </span>
        );
      }

      return (
        <span style={{
          color: amount === 0
            ? dynastyTheme.tokens.colors.neutral[400]
            : dynastyTheme.tokens.colors.warning,
          fontSize: '10px',
          fontWeight: '600',
          fontFamily: 'monospace'
        }}>
          ${Math.round(amount)}
        </span>
      );
    }
  });

  columns.push({
    key: 'market_value',
    title: 'MV',
    width: 36,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: true,
    sortValue: (p) => p?.league_context?.financial?.current_market_value ?? -Infinity,
    render: (player, rowType) => {
      if (rowType !== 'season') return null;

      const mv = player.league_context?.financial?.current_market_value;

      if (mv === null || mv === undefined) {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.neutral[500],
            fontSize: '10px',
            fontFamily: 'monospace'
          }}>
            -
          </span>
        );
      }

      return (
        <span style={{
          color: mv > 0
            ? dynastyTheme.tokens.colors.success
            : dynastyTheme.tokens.colors.neutral[400],
          fontSize: '10px',
          fontWeight: '600',
          fontFamily: 'monospace'
        }}>
          {mv > 0 ? `$${Math.round(mv)}` : 'N/A'}
        </span>
      );
    }
  });

  columns.push({
    key: 'action',
    title: 'Add',
    width: 45,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: false,
    render: (player, rowType, item) => {
      if (rowType !== 'season') {
        if (showAll) {
          const contractYears = player.league_context?.financial?.contract_years;
          if (contractYears) {
            return (
              <span style={{
                color: dynastyTheme.tokens.colors.neutral[400],
                fontSize: '9px',
                fontWeight: '600'
              }}>
                {contractYears}y
              </span>
            );
          }
        }
        return null;
      }

      const isOwned = player.league_context?.team?.team_name;
      const price = player.league_context?.financial?.baseline_price;
      const playerId = player.ids?.league_player;
      const isAdding = addingPlayer === playerId;

      if (isOwned) {
        return (
          <div style={{
            padding: '2px 4px',
            backgroundColor: dynastyTheme.tokens.colors.neutral[700],
            borderRadius: '3px',
            fontSize: '8px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[400],
            textTransform: 'uppercase'
          }}>
            OWNED
          </div>
        );
      }

      if (bulkMode) {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.success,
            fontSize: '10px',
            fontWeight: '600'
          }}>
            ${price.toFixed(1)}
          </span>
        );
      }

      // Action-button state semantics (Bible § 4.5.19, locked 2026-05-14):
      //   On waivers, period still active, viewer cut him  → CLRG  (disabled
      //                                                       cooling off)
      //   On waivers, period still active, other team cut → CLAIM (enabled —
      //                                                       claim queue)
      //   On waivers, period expired                       → CLAIM (enabled
      //                                                       for everyone)
      //   Not on waivers                                   → ADD   (enabled
      //                                                       normal FA add)
      //   In-flight add request                            → ...   (disabled
      //                                                       spinner)
      const onWaivers =
        player.league_context?.availability?.on_waivers === true ||
        player.league_context?.availability?.status === 'waivers';
      const waiverExpiresAt = player.league_context?.availability?.waiver_expires_at;
      const droppedByTeamId = player.league_context?.availability?.dropped_by_team_id;
      const isExpired = onWaivers && waiverExpiresAt
        ? new Date(waiverExpiresAt).getTime() <= Date.now()
        : false;
      const droppedByMyTeam = onWaivers && droppedByTeamId
        && myTeamId && droppedByTeamId === myTeamId;

      let buttonLabel;
      let buttonDisabled;
      if (isAdding) {
        buttonLabel = '...';
        buttonDisabled = true;
      } else if (onWaivers && !isExpired && droppedByMyTeam) {
        buttonLabel = 'CLRG';
        buttonDisabled = true;
      } else if (onWaivers && !isExpired && !droppedByMyTeam) {
        buttonLabel = 'CLAIM';
        buttonDisabled = false;
      } else if (onWaivers && isExpired) {
        buttonLabel = 'CLAIM';
        buttonDisabled = false;
      } else {
        buttonLabel = 'ADD';
        buttonDisabled = false;
      }

      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!buttonDisabled && onAddPlayer) {
              onAddPlayer(player);
            }
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!buttonDisabled && onAddPlayer) {
              onAddPlayer(player);
            }
          }}
          disabled={buttonDisabled}
          style={{
            padding: '3px 6px',
            backgroundColor: buttonDisabled ? dynastyTheme.tokens.colors.neutral[700] : dynastyTheme.tokens.colors.primary,
            color: C.black,
            border: 'none',
            borderRadius: '3px',
            fontSize: '9px',
            fontWeight: '700',
            cursor: buttonDisabled ? 'not-allowed' : 'pointer',
            opacity: buttonDisabled ? 0.5 : 1,
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            position: 'relative',
            zIndex: 1000
          }}
        >
          {buttonLabel}
        </button>
      );
    }
  });

  return columns;
};

// =============================================================================
// PITCHER COLUMNS (2-Row Format)
// =============================================================================

export const createMobilePitcherColumns = ({
  onAddPlayer,
  addingPlayer,
  showAll = false,
  bulkMode = false,
  isPlayerSelected,
  togglePlayerSelection,
  isCommissionerMode = false,
  activeTeamName = '',
  myTeamId = null,
  dateRange = 'season',
  onPlayerClick = null,
  statConfigs = [],
}) => {
  // 9o.6.e Commit 8: see hitter factory above for the helper's rationale.
  const statsSlot = STATS_KEY_FOR_RANGE[dateRange] || 'season';
  const k = (category, field) => `stats.${statsSlot}.${category}.${field}`;

  const columns = [];

  // Checkbox column (bulk mode only)
  if (bulkMode) {
    columns.push({
      key: 'checkbox',
      title: '☑',
      width: 24,
      sticky: true,
      cellPadding: 'py-0 px-0',
      textAlign: 'text-center',
      sortable: false,
      render: (player, rowType) => {
        if (rowType === 'rolling') return null;

        const isOwned = player.league_context?.team?.team_name;
        const isSelected = isPlayerSelected?.(player);

        if (isOwned) {
          return (
            <div style={{
              width: '24px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.3
            }}>
              <input
                type="checkbox"
                disabled
                style={{ width: '14px', height: '14px', cursor: 'not-allowed' }}
              />
            </div>
          );
        }

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              togglePlayerSelection?.(player);
            }}
            style={{
              width: '24px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                togglePlayerSelection?.(player);
              }}
              style={{ width: '14px', height: '14px', cursor: 'pointer' }}
            />
          </div>
        );
      }
    });
  }

  // POS Column (Sticky)
  columns.push({
    key: 'position_slot',
    title: 'POS',
    width: 22,
    sticky: true,
    cellPadding: 'py-0 px-0',
    textAlign: 'text-center',
    sortable: false,
    render: (player, rowType) => {
      if (rowType === 'rolling') return null;

      const position = player.info?.position || '-';
      return (
        <span style={{
          color: dynastyTheme.tokens.colors.warning,
          fontWeight: '700',
          fontSize: '9px'
        }}>
          {position}
        </span>
      );
    }
  });

  // Player Name Column (Sticky). Width 130 — see hitter block for rationale.
  columns.push({
    key: 'player_name',
    title: 'Player',
    width: 130,
    sticky: true,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-left',
    sortable: true,
    sortValue: (p) => `${p?.info?.last_name || ''} ${p?.info?.first_name || ''}`.trim(),
    render: (player, rowType) => {
      if (rowType === 'season') {
        const firstName = player.info?.first_name || '';
        const lastName = player.info?.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const isOnWaivers = player.league_context?.availability?.on_waivers === true
          || player.league_context?.availability?.status === 'waivers';
        const waiverLabel = player.league_context?.availability?.waiver_label || 'W';

        return (
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
          }}>
            <MobilePlayerLink
              mlbId={player.ids?.mlb}
              name={fullName}
              onClick={onPlayerClick ? () => onPlayerClick(player) : null}
              style={{ fontSize: 11, fontWeight: 500 }}
            />
            {isOnWaivers && (
              <span style={{ flexShrink: 0, fontSize: 8, color: C.orange.soft, border: `1px solid ${withAlpha(C.orange.soft, 0.5)}`, borderRadius: 2, padding: '0 2px', fontStyle: 'normal', whiteSpace: 'nowrap' }}>
                {waiverLabel}
              </span>
            )}
            <MobileInjuryBadge mlbId={player.ids?.mlb} />
          </span>
        );
      } else {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.warning,
            fontSize: '9px',
            fontStyle: 'italic',
            opacity: 0.6
          }}>
            {getRollingRowLabel(dateRange) || 'Last 14'}
          </span>
        );
      }
    }
  });

  // MLB Team Column — width 22 (down from 26) — see hitter block.
  columns.push({
    key: 'mlb_team',
    title: 'Tm',
    width: 22,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: false,
    render: (player, rowType) => {
      if (rowType === 'rolling') return null;

      const team = player.info?.mlb_team || '-';
      return (
        <span style={{
          color: dynastyTheme.tokens.colors.neutral[400],
          fontSize: '9px'
        }}>
          {team}
        </span>
      );
    }
  });

  // Games Started
  columns.push({
    key: k('pitching', 'games_started'),
    title: 'GS',
    width: 24,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: true,
    render: (player, rowType) => {
      const stats = rowType === 'season'
        ? player.stats?.season?.pitching
        : getRollingStatsForWindow(player, dateRange)?.pitching;
      const value = stats?.games_started ?? 0;

      const colorClass = rowType === 'season' ? C.white : dynastyTheme.tokens.colors.warning;
      const fontStyle = rowType === 'season' ? 'normal' : 'italic';
      const opacity = rowType === 'season' ? 1 : 0.6;

      return (
        <span style={{
          color: colorClass,
          fontSize: '10px',
          fontStyle: fontStyle,
          opacity: opacity,
          fontFamily: 'monospace'
        }}>
          {value}
        </span>
      );
    }
  });

  // === Dynamic scoring-category columns (Bible § 14 — driven by
  // league_settings.scoring_categories). See hitter factory for behavior.
  // Closes the missing-QS/L/HLD/BS bug: any canonical pitching stat in
  // statMapping now renders when the league scores it.
  // DD-STAT-CONSOLIDATION sweep: exclude dynamic GS — the static column renders it.
  statConfigs
    .filter((c) => c.category === 'pitching' && statFieldSimple(c) !== 'games_started')
    .forEach((c) => {
      columns.push(
        buildDynamicStatColumn(c, {
          statsSlot,
          getRollingStats: (p, cat) => getRollingStatsForWindow(p, dateRange)?.[cat],
          styledRender: true,
        }),
      );
    });

  // Team Column (All Players mode only)
  if (showAll) {
    columns.push({
      key: 'fantasy_team',
      title: 'Team',
      width: 65,
      cellPadding: 'py-0 px-1',
      textAlign: 'text-left',
      sortable: false,
      render: (player, rowType) => {
        if (rowType === 'rolling') return null;

        const teamName = player.league_context?.team?.team_name || '';
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.neutral[400],
            fontSize: '9px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {teamName}
          </span>
        );
      }
    });
  }

  // Price / MV / Add render on the always-visible season row.
  // Matches desktop TableColumns.js gating; rolling row holds rolling stats only.
  columns.push({
    key: 'price',
    title: 'Price',
    width: 42,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: true,
    sortValue: (p) => p?.league_context?.financial?.baseline_price ?? -Infinity,
    render: (player, rowType) => {
      if (rowType !== 'season') return null;

      const amount = player.league_context?.financial?.baseline_price;

      if (amount === null || amount === undefined) {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.neutral[500],
            fontSize: '10px',
            fontFamily: 'monospace'
          }}>
            -
          </span>
        );
      }

      return (
        <span style={{
          color: amount === 0
            ? dynastyTheme.tokens.colors.neutral[400]
            : dynastyTheme.tokens.colors.warning,
          fontSize: '10px',
          fontWeight: '600',
          fontFamily: 'monospace'
        }}>
          ${Math.round(amount)}
        </span>
      );
    }
  });

  columns.push({
    key: 'market_value',
    title: 'MV',
    width: 36,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: true,
    sortValue: (p) => p?.league_context?.financial?.current_market_value ?? -Infinity,
    render: (player, rowType) => {
      if (rowType !== 'season') return null;

      const mv = player.league_context?.financial?.current_market_value;

      if (mv === null || mv === undefined) {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.neutral[500],
            fontSize: '10px',
            fontFamily: 'monospace'
          }}>
            -
          </span>
        );
      }

      return (
        <span style={{
          color: mv > 0
            ? dynastyTheme.tokens.colors.success
            : dynastyTheme.tokens.colors.neutral[400],
          fontSize: '10px',
          fontWeight: '600',
          fontFamily: 'monospace'
        }}>
          {mv > 0 ? `$${Math.round(mv)}` : 'N/A'}
        </span>
      );
    }
  });

  columns.push({
    key: 'action',
    title: 'Add',
    width: 45,
    cellPadding: 'py-0 px-1',
    textAlign: 'text-center',
    sortable: false,
    render: (player, rowType, item) => {
      if (rowType !== 'season') {
        if (showAll) {
          const contractYears = player.league_context?.financial?.contract_years;
          if (contractYears) {
            return (
              <span style={{
                color: dynastyTheme.tokens.colors.neutral[400],
                fontSize: '9px',
                fontWeight: '600'
              }}>
                {contractYears}y
              </span>
            );
          }
        }
        return null;
      }

      const isOwned = player.league_context?.team?.team_name;
      const price = player.league_context?.financial?.baseline_price;
      const playerId = player.ids?.league_player;
      const isAdding = addingPlayer === playerId;

      if (isOwned) {
        return (
          <div style={{
            padding: '2px 4px',
            backgroundColor: dynastyTheme.tokens.colors.neutral[700],
            borderRadius: '3px',
            fontSize: '8px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[400],
            textTransform: 'uppercase'
          }}>
            OWNED
          </div>
        );
      }

      if (bulkMode) {
        return (
          <span style={{
            color: dynastyTheme.tokens.colors.success,
            fontSize: '10px',
            fontWeight: '600'
          }}>
            ${price.toFixed(1)}
          </span>
        );
      }

      // Action-button state semantics (Bible § 4.5.19, locked 2026-05-14):
      //   On waivers, period still active, viewer cut him  → CLRG  (disabled
      //                                                       cooling off)
      //   On waivers, period still active, other team cut → CLAIM (enabled —
      //                                                       claim queue)
      //   On waivers, period expired                       → CLAIM (enabled
      //                                                       for everyone)
      //   Not on waivers                                   → ADD   (enabled
      //                                                       normal FA add)
      //   In-flight add request                            → ...   (disabled
      //                                                       spinner)
      const onWaivers =
        player.league_context?.availability?.on_waivers === true ||
        player.league_context?.availability?.status === 'waivers';
      const waiverExpiresAt = player.league_context?.availability?.waiver_expires_at;
      const droppedByTeamId = player.league_context?.availability?.dropped_by_team_id;
      const isExpired = onWaivers && waiverExpiresAt
        ? new Date(waiverExpiresAt).getTime() <= Date.now()
        : false;
      const droppedByMyTeam = onWaivers && droppedByTeamId
        && myTeamId && droppedByTeamId === myTeamId;

      let buttonLabel;
      let buttonDisabled;
      if (isAdding) {
        buttonLabel = '...';
        buttonDisabled = true;
      } else if (onWaivers && !isExpired && droppedByMyTeam) {
        buttonLabel = 'CLRG';
        buttonDisabled = true;
      } else if (onWaivers && !isExpired && !droppedByMyTeam) {
        buttonLabel = 'CLAIM';
        buttonDisabled = false;
      } else if (onWaivers && isExpired) {
        buttonLabel = 'CLAIM';
        buttonDisabled = false;
      } else {
        buttonLabel = 'ADD';
        buttonDisabled = false;
      }

      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!buttonDisabled && onAddPlayer) {
              onAddPlayer(player);
            }
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!buttonDisabled && onAddPlayer) {
              onAddPlayer(player);
            }
          }}
          disabled={buttonDisabled}
          style={{
            padding: '3px 6px',
            backgroundColor: buttonDisabled ? dynastyTheme.tokens.colors.neutral[700] : dynastyTheme.tokens.colors.primary,
            color: C.black,
            border: 'none',
            borderRadius: '3px',
            fontSize: '9px',
            fontWeight: '700',
            cursor: buttonDisabled ? 'not-allowed' : 'pointer',
            opacity: buttonDisabled ? 0.5 : 1,
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            position: 'relative',
            zIndex: 1000
          }}
        >
          {buttonLabel}
        </button>
      );
    }
  });

  return columns;
};
